import {
  HomeBreakpointKey,
  PackedMainLayout,
  PlacedWidget,
  WidgetColSpans,
  WidgetLayoutProfile,
  WidgetRowLayout,
} from './widgetLayout.types';
import {
  HOME_LAYOUT_BY_BREAKPOINT,
  HOME_LG_MAX_CARDS_PER_ROW,
  HOME_MD_MAX_CARDS_PER_ROW,
  HOME_SM_MAX_CARDS_PER_ROW,
  HOME_XL_MAX_CARDS_PER_ROW,
  HomeLayoutBreakpointConfig,
} from './widgetLayoutConstants';

export const MANAGE_WIDGET_ID = 'home-manage-widget';

export type PackWidgetsInput = {
  hideableWidgetIds: string[];
  layoutById: Record<string, WidgetLayoutProfile>;
  breakpoint: HomeBreakpointKey;
};

const getColSpan = (
  colSpan: WidgetColSpans,
  breakpoint: HomeBreakpointKey,
  mainCols: number
): number => {
  const span =
    colSpan[breakpoint] ??
    (breakpoint === 'sm' ? colSpan.xs : undefined) ??
    colSpan.lg ??
    colSpan.md ??
    colSpan.xs ??
    mainCols;

  return Math.min(Math.max(span, 1), mainCols);
};

const sortHideableIds = (
  hideableWidgetIds: string[],
  layoutById: Record<string, WidgetLayoutProfile>
) =>
  [...hideableWidgetIds].sort((a, b) => {
    const orderA = layoutById[a]?.order ?? 0;
    const orderB = layoutById[b]?.order ?? 0;
    return orderA - orderB;
  });

/**
 * Split widgets into rows of at most `maxCardsPerRow`, avoiding a lonely
 * last-row card when it would stretch wider than siblings (e.g. XL 4 → 2+2
 * instead of 3+1). Only reduces a full row when the shortened row still has
 * at least 2 cards (so LG max=2 keeps 3 → 2+1).
 */
const getBalancedRowSizes = (
  totalCount: number,
  maxCardsPerRow: number
): number[] => {
  const sizes: number[] = [];
  let remaining = totalCount;

  while (remaining > 0) {
    let take = Math.min(maxCardsPerRow, remaining);

    if (remaining - take === 1 && take - 1 >= 2) {
      take -= 1;
    }

    sizes.push(take);
    remaining -= take;
  }

  return sizes;
};

const packMainRows = (
  hideableWidgetIds: string[],
  includeManageWidget: boolean,
  maxCardsPerRow: number
): WidgetRowLayout[] => {
  const widgetIds = includeManageWidget
    ? [...hideableWidgetIds, MANAGE_WIDGET_ID]
    : [...hideableWidgetIds];
  const rowSizes = getBalancedRowSizes(widgetIds.length, maxCardsPerRow);
  const rows: WidgetRowLayout[] = [];
  let index = 0;

  rowSizes.forEach((size) => {
    rows.push({
      row: rows.length + 1,
      widgetIds: widgetIds.slice(index, index + size),
    });
    index += size;
  });

  return rows;
};

const getMaxCardsPerRow = (breakpoint: HomeBreakpointKey): number | null => {
  if (breakpoint === 'xl') {
    return HOME_XL_MAX_CARDS_PER_ROW;
  }

  if (breakpoint === 'lg') {
    return HOME_LG_MAX_CARDS_PER_ROW;
  }

  if (breakpoint === 'md') {
    return HOME_MD_MAX_CARDS_PER_ROW;
  }

  if (breakpoint === 'sm') {
    return HOME_SM_MAX_CARDS_PER_ROW;
  }

  return null;
};

const packIntoColumns = (
  widgetIds: string[],
  layoutById: Record<string, WidgetLayoutProfile>,
  breakpoint: HomeBreakpointKey,
  mainCols: number,
  startRow: number
): PlacedWidget[] => {
  const placed: PlacedWidget[] = [];
  let col = 0;
  let row = startRow;

  widgetIds.forEach((id) => {
    const profile = layoutById[id];
    if (!profile) {
      return;
    }

    const colSpan = getColSpan(profile.colSpan, breakpoint, mainCols);

    if (col > 0 && col + colSpan > mainCols) {
      row += 1;
      col = 0;
    }

    placed.push({
      id,
      zone: 'main',
      col,
      row,
      colSpan,
    });

    col += colSpan;

    if (col >= mainCols) {
      row += 1;
      col = 0;
    }
  });

  return placed;
};

const packManageWidget = (
  hideablePlaced: PlacedWidget[],
  layoutById: Record<string, WidgetLayoutProfile>,
  breakpoint: HomeBreakpointKey,
  mainCols: number
): PlacedWidget | null => {
  const manageProfile = layoutById[MANAGE_WIDGET_ID];
  if (!manageProfile) {
    return null;
  }

  const manageSpan = getColSpan(manageProfile.colSpan, breakpoint, mainCols);

  const lastRow = hideablePlaced.reduce(
    (max, widget) => Math.max(max, widget.row),
    0
  );
  const widgetsOnLastRow = hideablePlaced.filter(
    (widget) => widget.row === lastRow
  );
  const usedColsOnLastRow = widgetsOnLastRow.reduce(
    (sum, widget) => sum + widget.colSpan,
    0
  );

  let manageCol = 0;
  let manageRow = lastRow;

  if (usedColsOnLastRow > 0 && usedColsOnLastRow + manageSpan > mainCols) {
    manageRow = lastRow + 1;
    manageCol = 0;
  } else if (usedColsOnLastRow > 0) {
    manageCol = usedColsOnLastRow;
    manageRow = lastRow;
  } else if (hideablePlaced.length === 0) {
    manageRow = 1;
    manageCol = 0;
  }

  return {
    id: MANAGE_WIDGET_ID,
    zone: 'main',
    col: manageCol,
    row: manageRow,
    colSpan: manageSpan,
  };
};

export const packMainLayout = ({
  hideableWidgetIds,
  layoutById,
  breakpoint,
}: PackWidgetsInput): PackedMainLayout => {
  const config: HomeLayoutBreakpointConfig =
    HOME_LAYOUT_BY_BREAKPOINT[breakpoint];
  const { mainCols } = config;
  const sortedHideableIds = sortHideableIds(hideableWidgetIds, layoutById);

  const hideablePlaced = packIntoColumns(
    sortedHideableIds,
    layoutById,
    breakpoint,
    mainCols,
    1
  );
  const managePlacement = packManageWidget(
    hideablePlaced,
    layoutById,
    breakpoint,
    mainCols
  );

  const gridPlacements = managePlacement
    ? [...hideablePlaced, managePlacement]
    : hideablePlaced;

  const maxCardsPerRow = getMaxCardsPerRow(breakpoint);

  return {
    widgetRows:
      maxCardsPerRow !== null
        ? packMainRows(
            sortedHideableIds,
            Boolean(layoutById[MANAGE_WIDGET_ID]),
            maxCardsPerRow
          )
        : [],
    gridPlacements,
  };
};

/** @deprecated Use packMainLayout — kept for tests. */
export const packMainWidgets = (input: PackWidgetsInput): PlacedWidget[] =>
  packMainLayout(input).gridPlacements;
